// App.tsx
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ProfilePage from './pages/ProfilePage';
import HomePage from './pages/HomePage';
import { useAuth } from './auth/AuthContext';
import ProtectedRoute from './auth/ProtectedRoute';
import { Box, CircularProgress } from '@mui/material';
import MainLayout from './layout/MainLayout';
import EditPhotosPage from './pages/EditPhotosPage';
import ChatPage from './pages/ChatPage';
import MatchedUsersPage from './pages/MatchedUsersPage';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { requestFcmToken, getMessagingIfSupported } from './firebase/firebase-config';
import { onMessage } from 'firebase/messaging';
import CustomNotification from './components/CustomNotification';
import { CssBaseline } from '@mui/material';
import AdminLayout from './admin/layout/AdminLayout';
import LanguageManager from './admin/pages/lang/LanguageManager';
import TranslationKeyManager from './admin/pages/lang/TranslationKeyManager';
import TranslationValueManager from './admin/pages/lang/TranslationValueManager';
import { LanguageProvider } from './admin/contexts/LanguageContext';
import { RolesPage } from './admin/pages/role/RolesPage';
import { UserRolesPage } from './admin/pages/role/UserRolesPage';
import { RightsPage } from './admin/pages/right/RightsPage';
import { RoleRightsPage } from './admin/pages/right/RoleRightsPage';

const NotificationHandler: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupFcm = async () => {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

      try {
        const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        const token = await requestFcmToken(swRegistration);
        if (token) {
          console.log('✅ FCM Token:', token);
        }

        const msg = await getMessagingIfSupported();
        if (!msg) return;

        unsubscribe = onMessage(msg, (payload) => {
          const title = payload.data?.title || 'Bildirim';
          const body = payload.data?.body || '';
          const icon = payload.data?.icon || '/icon.png';
          const url = payload.data?.link || '/';

          console.log('📥 onMessage mesaj geldi:', payload);
          console.log('📥 title:', title);
          console.log('📥 body', body);
          console.log('📥 icon', icon);
          console.log('📥 url', url);
          if (title && body) {
            enqueueSnackbar('', {
              variant: 'default',
              autoHideDuration: 3000,
              content: (key) => (
                <CustomNotification
                  title={title}
                  body={body}
                  icon={icon}
                  url={url}
                />
              ),
            });
          }
        });
      } catch (err) {
        console.error('SW ya da FCM kurulumu başarısız:', err);
      }
    };

    setupFcm();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [enqueueSnackbar]);

  return null;
};

const App: React.FC = () => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LanguageProvider>
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <CssBaseline />
        <NotificationHandler />
        <Box sx={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Protected Routes with MainLayout */}
            <Route element={<ProtectedRoute />}>
              <Route element={<MainLayout />}>
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/profile/edit-photos" element={<EditPhotosPage />} />
                <Route path="/messages" element={<MatchedUsersPage />} />
                <Route path="/messages/:userId" element={<ChatPage />} />
              </Route>
            </Route>
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route path="languages" element={<LanguageManager />} />
              <Route path="translation-keys" element={<TranslationKeyManager />} />
              <Route path="translation-values" element={<TranslationValueManager />} />
              <Route path="roles" element={<RolesPage />} />
              <Route path="user-roles" element={<UserRolesPage />} />
              <Route path="rights" element={<RightsPage />} />
              <Route path="role-rights" element={<RoleRightsPage />} />
            </Route>

            {/* Catch-All */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Box>
      </SnackbarProvider>
    </LanguageProvider>
  );
};

export default App;